import { useState, useEffect } from 'react';
import tmdbService from '../services/tmdbApi';
import { enhanceMovieWithPosters } from '../utils/posterUtils';

/**
 * Custom hook for fetching and managing latest movie data
 */
export const useLatestMovie = () => {
  const [latestMovie, setLatestMovie] = useState({
    movie: null,
    loading: true,
    error: null
  });

  const fetchLatestMovie = async () => {
    setLatestMovie(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      console.log("🚀 Fetching latest movie...");
      
      // Use the TMDB service to get now playing movies (latest)
      const data = await tmdbService.getNowPlayingMovies({ page: 1 });
      
      if (data && data.results && data.results.length > 0) {
        // Get the first movie (most recent)
        const rawMovie = data.results[0];
        console.log("📽️ Raw latest movie data:", rawMovie);
        
        // Enhance with poster URLs and additional formatting
        const enhancedMovie = enhanceMovieWithPosters(rawMovie);
        
        // Add additional formatted data
        const formattedMovie = {
          ...enhancedMovie,
          // Formatted data for display
          ageRating: rawMovie.adult ? '18+' : '13+',
          duration: rawMovie.runtime 
            ? `${Math.floor(rawMovie.runtime / 60)}h ${rawMovie.runtime % 60}m` 
            : '2h 30m',
          year: rawMovie.release_date 
            ? rawMovie.release_date.substring(0, 4) 
            : new Date().getFullYear().toString(),
          rating: rawMovie.vote_average 
            ? rawMovie.vote_average.toFixed(1) 
            : '7.5',
          // Ensure we have the main poster_url
          poster_url: enhancedMovie.poster_url
        };

        console.log("✨ Enhanced latest movie:", formattedMovie);

        setLatestMovie({
          movie: formattedMovie,
          loading: false,
          error: null
        });

        return formattedMovie;
      } else {
        throw new Error('No latest movies found');
      }
    } catch (error) {
      console.error("❌ Error fetching latest movie:", error);
      
      // Set fallback movie data
      const fallbackMovie = {
        id: 550,
        title: 'Fight Club',
        overview: 'A ticking-time-bomb insomniac and a slippery soap salesman channel primal male aggression into a shocking new form of therapy.',
        release_date: '1999-10-15',
        vote_average: 8.4,
        poster_path: '/pB8BM7pdSp6B6Ih7QZ4DrQ3PmJK.jpg',
        backdrop_path: '/87hTDiay2N2qWyX4Dx7d0T6bqlh.jpg',
        adult: false,
        runtime: 139,
        // Enhanced poster URLs
        poster_url: 'https://image.tmdb.org/t/p/w500/pB8BM7pdSp6B6Ih7QZ4DrQ3PmJK.jpg',
        poster_url_thumbnail: 'https://image.tmdb.org/t/p/w92/pB8BM7pdSp6B6Ih7QZ4DrQ3PmJK.jpg',
        poster_url_small: 'https://image.tmdb.org/t/p/w154/pB8BM7pdSp6B6Ih7QZ4DrQ3PmJK.jpg',
        poster_url_medium: 'https://image.tmdb.org/t/p/w342/pB8BM7pdSp6B6Ih7QZ4DrQ3PmJK.jpg',
        poster_url_large: 'https://image.tmdb.org/t/p/w500/pB8BM7pdSp6B6Ih7QZ4DrQ3PmJK.jpg',
        poster_url_original: 'https://image.tmdb.org/t/p/original/pB8BM7pdSp6B6Ih7QZ4DrQ3PmJK.jpg',
        backdrop_url: 'https://image.tmdb.org/t/p/w1280/87hTDiay2N2qWyX4Dx7d0T6bqlh.jpg',
        ageRating: '18+',
        duration: '2h 19m',
        year: '1999',
        rating: '8.4'
      };

      setLatestMovie({
        movie: fallbackMovie,
        loading: false,
        error: error.message
      });

      return fallbackMovie;
    }
  };

  // Fetch latest movie on component mount
  useEffect(() => {
    fetchLatestMovie();
  }, []);

  // Function to refresh latest movie
  const refreshLatestMovie = () => {
    fetchLatestMovie();
  };

  // Function to get poster URL by size
  const getPosterUrlBySize = (size = 'w500') => {
    if (!latestMovie.movie) return null;
    
    const sizeMap = {
      thumbnail: latestMovie.movie.poster_url_thumbnail,
      small: latestMovie.movie.poster_url_small,
      medium: latestMovie.movie.poster_url_medium,
      large: latestMovie.movie.poster_url_large,
      original: latestMovie.movie.poster_url_original
    };

    return sizeMap[size] || latestMovie.movie.poster_url;
  };

  return {
    latestMovie: latestMovie.movie,
    loading: latestMovie.loading,
    error: latestMovie.error,
    refreshLatestMovie,
    getPosterUrlBySize
  };
};

export default useLatestMovie;
